import { BlindBindError, errorForClose } from "./errors.js";
import type { OwnedBytes } from "./bytes.js";

type SocketEventType = "open" | "message" | "error" | "close";
const maxQueuedMessages = 128;
const maxQueuedBytes = 4 << 20;

export interface WebSocketLike {
  binaryType: BinaryType;
  readonly readyState: number;
  readonly bufferedAmount: number;
  readonly protocol?: string;

  send(data: string | Blob | BufferSource): void;
  close(code?: number, reason?: string): void;

  addEventListener(type: SocketEventType, listener: EventListener): void;
  removeEventListener(type: SocketEventType, listener: EventListener): void;
}

export interface WebSocketFactory {
  connect(url: string, protocols?: readonly string[]): WebSocketLike;
}

export interface OpenedSocket {
  socket: WebSocketLike;
  inbox: SocketInbox;
}

interface InboxWaiter {
  resolve(value: unknown): void;
  reject(error: unknown): void;
  cleanup(): void;
}

export class SocketInbox {
  readonly #socket: WebSocketLike;
  readonly #queue: unknown[] = [];
  readonly #waiters: InboxWaiter[] = [];
  #closedError: BlindBindError | null = null;
  #queuedBytes = 0;

  constructor(socket: WebSocketLike) {
    this.#socket = socket;
    socket.addEventListener("message", this.#onMessage);
    socket.addEventListener("close", this.#onClose);
    socket.addEventListener("error", this.#onError);
  }

  next(options: { signal?: AbortSignal; timeoutMs?: number } = {}): Promise<unknown> {
    if (this.#queue.length > 0) {
      const value = this.#queue.shift();
      this.#queuedBytes -= socketValueBytes(value);
      return Promise.resolve(value);
    }
    if (this.#closedError) return Promise.reject(this.#closedError);
    if (options.signal?.aborted) return Promise.reject(new DOMException("Operation aborted", "AbortError"));
    return new Promise((resolve, reject) => {
      let timeout: ReturnType<typeof setTimeout> | undefined;
      const abort = () => finishReject(new DOMException("Operation aborted", "AbortError"));
      const cleanup = () => {
        if (timeout !== undefined) clearTimeout(timeout);
        options.signal?.removeEventListener("abort", abort);
      };
      const waiter: InboxWaiter = { resolve: finishResolve, reject: finishReject, cleanup };
      const remove = () => {
        const index = this.#waiters.indexOf(waiter);
        if (index >= 0) this.#waiters.splice(index, 1);
      };
      function finishResolve(value: unknown): void {
        remove();
        cleanup();
        resolve(value);
      }
      function finishReject(error: unknown): void {
        remove();
        cleanup();
        reject(error);
      }
      options.signal?.addEventListener("abort", abort, { once: true });
      if (options.timeoutMs !== undefined) {
        timeout = setTimeout(
          () => finishReject(new BlindBindError("connection_failed", "WebSocket message timed out")),
          options.timeoutMs,
        );
      }
      this.#waiters.push(waiter);
    });
  }

  async nextBytes(options: { signal?: AbortSignal; timeoutMs?: number } = {}): Promise<OwnedBytes> {
    const value = await this.next(options);
    if (value instanceof ArrayBuffer) return new Uint8Array(value.slice(0));
    if (ArrayBuffer.isView(value)) {
      const result = new Uint8Array(value.byteLength);
      result.set(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
      return result;
    }
    if (value instanceof Blob) return new Uint8Array(await value.arrayBuffer());
    throw new BlindBindError("protocol_error", "Expected a binary WebSocket message");
  }

  async nextText(options: { signal?: AbortSignal; timeoutMs?: number } = {}): Promise<string> {
    const value = await this.next(options);
    if (typeof value !== "string") throw new BlindBindError("protocol_error", "Expected a text WebSocket message");
    return value;
  }

  detach(): void {
    this.#socket.removeEventListener("message", this.#onMessage);
    this.#socket.removeEventListener("close", this.#onClose);
    this.#socket.removeEventListener("error", this.#onError);
  }

  readonly #onMessage: EventListener = (event) => {
    if (!(event instanceof MessageEvent)) return;
    const waiter = this.#waiters.shift();
    if (waiter) {
      waiter.cleanup();
      waiter.resolve(event.data);
      return;
    }
    const bytes = socketValueBytes(event.data);
    if (this.#queue.length >= maxQueuedMessages || this.#queuedBytes + bytes > maxQueuedBytes) {
      const error = new BlindBindError("busy", "WebSocket receive queue exceeded its bounded capacity");
      this.#fail(error);
      this.#socket.close(1009, error.message);
      return;
    }
    this.#queue.push(event.data);
    this.#queuedBytes += bytes;
  };

  readonly #onClose: EventListener = (event) => {
    const close = event instanceof CloseEvent ? event : null;
    this.#fail(errorForClose(close?.code ?? 1006, close?.reason ?? ""));
  };

  readonly #onError: EventListener = () => {
    if (this.#socket.readyState === 3)
      this.#fail(new BlindBindError("connection_failed", "WebSocket connection failed"));
  };

  #fail(error: BlindBindError): void {
    if (this.#closedError) return;
    this.#closedError = error;
    for (const waiter of this.#waiters.splice(0)) {
      waiter.cleanup();
      waiter.reject(error);
    }
  }
}

export async function openWebSocket(
  url: string,
  options: { factory?: WebSocketFactory; signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<OpenedSocket> {
  const socket = options.factory?.connect(url) ?? defaultWebSocketFactory().connect(url);
  socket.binaryType = "arraybuffer";
  const inbox = new SocketInbox(socket);
  if (options.signal?.aborted) {
    socket.close();
    throw new DOMException("Operation aborted", "AbortError");
  }
  if (socket.readyState === 1) return { socket, inbox };
  await new Promise<void>((resolve, reject) => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const cleanup = () => {
      if (timeout !== undefined) clearTimeout(timeout);
      options.signal?.removeEventListener("abort", abort);
      socket.removeEventListener("open", opened);
      socket.removeEventListener("close", closed);
      socket.removeEventListener("error", failed);
    };
    const opened: EventListener = () => {
      cleanup();
      resolve();
    };
    const closed: EventListener = (event) => {
      cleanup();
      const close = event instanceof CloseEvent ? event : null;
      reject(errorForClose(close?.code ?? 1006, close?.reason ?? ""));
    };
    const failed: EventListener = () => {
      cleanup();
      reject(new BlindBindError("connection_failed", "WebSocket connection failed"));
    };
    const abort = () => {
      cleanup();
      socket.close();
      reject(new DOMException("Operation aborted", "AbortError"));
    };
    socket.addEventListener("open", opened);
    socket.addEventListener("close", closed);
    socket.addEventListener("error", failed);
    options.signal?.addEventListener("abort", abort, { once: true });
    timeout = setTimeout(() => {
      cleanup();
      socket.close();
      reject(new BlindBindError("connection_failed", "WebSocket connection timed out"));
    }, options.timeoutMs ?? 10_000);
  });
  return { socket, inbox };
}

function defaultWebSocketFactory(): WebSocketFactory {
  const Socket = globalThis.WebSocket;
  if (!Socket) throw new BlindBindError("connection_failed", "WebSocket is unavailable in this runtime");
  return { connect: (url, protocols) => new Socket(url, protocols ? [...protocols] : undefined) };
}

function socketValueBytes(value: unknown): number {
  if (typeof value === "string") return value.length * 2;
  if (value instanceof ArrayBuffer) return value.byteLength;
  if (ArrayBuffer.isView(value)) return value.byteLength;
  if (value instanceof Blob) return value.size;
  return 0;
}
